import { useMemo } from 'react'
import { useSelectUserMarket } from '../state/selectors'
import { isHealthCallsEnabled } from '../utils/market'
import { CONTENTFUL_ENTRY_IDS } from '../constants/contentfulEntries'

/**
 * Hook to filter content blocks based on market availability
 *
 * This hook centralizes the logic for filtering out market-specific content.
 * Currently filters out HEALTH_CALL content for non-Swedish markets.
 *
 * @param items - Array of content items to filter (must have sys.id property)
 * @returns Filtered array of content items appropriate for the user's market
 *
 * @example
 * const filteredBlocks = useFilteredContent(contentBlocks)
 */
export const useFilteredContent = <T extends { sys?: { id?: string } }>(items: T[] | undefined): T[] => {
  const userMarket = useSelectUserMarket()

  return useMemo(() => {
    if (!items) return []

    // If health calls are enabled for this market, show all content
    if (isHealthCallsEnabled(userMarket)) return items

    // Filter out HEALTH_CALL content for markets where it's not available
    const healthCallIds: string[] = [
      CONTENTFUL_ENTRY_IDS.HEALTH_CALL_FAQ,
      CONTENTFUL_ENTRY_IDS.ABOUT_DIGITAL_HEALTH_CALL,
      CONTENTFUL_ENTRY_IDS.HEALTH_CALL,
      CONTENTFUL_ENTRY_IDS.DIGITAL_HEALTH_CALL,
    ]

    return items.filter((item) => {
      const itemId = item.sys?.id
      if (!itemId) return true
      return !healthCallIds.includes(itemId)
    })
  }, [items, userMarket])
}
